import { useTranslation } from 'react-i18next';
import { Loader2, RotateCw } from 'lucide-react';
import { CheckCircleSolid, ClockCircleSolid, DangerCircleSolid } from '@mynaui/icons-react';

import { useDialog } from '@/providers/DialogProvider';
import { Button } from '@/components/ui/button';
import { RunDeviationAnalysisDialog } from '@/components/RunDeviationAnalysisDialog';
import { formatDateTime } from '@/components/BatchList';
import { useFeatureCheck } from '@/hooks/useFeature';
import type { DeviationJobsQuery, LayersQuery } from '@/graphql/graphql';

type Job = DeviationJobsQuery['deviationJobs'][number];
type LayerData = LayersQuery['layers'][number];

interface Props {
  job: Job;
  layersById: Record<string, LayerData>;
  candidateReferenceLayers: LayerData[];
  projectId: string;
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'WAITING') return <ClockCircleSolid className='size-5 text-amber-500' />;
  if (status === 'PROCESSING') return <Loader2 className='size-5 animate-spin text-blue-600' />;
  if (status === 'PROCESSED') return <CheckCircleSolid className='size-5 text-emerald-500' />;
  if (status === 'FAILED') return <DangerCircleSolid className='size-5 text-rose-500' />;
  return <ClockCircleSolid className='size-5 text-amber-500' />;
}

export function AnalysisRow({ job, layersById, candidateReferenceLayers, projectId }: Props) {
  const { t } = useTranslation('project');
  const { openDialog } = useDialog();
  const featureCheck = useFeatureCheck();

  const params = ((job.inputJson as Record<string, unknown>)?.params ?? {}) as Record<string, unknown>;
  const stats = ((job.outputJson as Record<string, unknown>)?.stats ?? {}) as Record<string, unknown>;
  const sourceLayerId = (job.inputJson as Record<string, unknown>)?.sourceLayerId as string | undefined;
  const referenceLayerId = (job.inputJson as Record<string, unknown>)?.referenceLayerId as string | undefined;

  const sourceLayer = sourceLayerId ? layersById[sourceLayerId] : undefined;
  const referenceLayer = referenceLayerId ? layersById[referenceLayerId] : undefined;

  const handleRerun = () => {
    if (!sourceLayer) return;
    const gate = featureCheck('analysis.deviation');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    openDialog(
      <RunDeviationAnalysisDialog
        sourceLayer={sourceLayer}
        candidateReferenceLayers={candidateReferenceLayers.filter(
          (l) => l.id !== sourceLayer.id,
        )}
        projectId={projectId}
        initialValues={{
          referenceLayerId: referenceLayerId ?? '',
          mode: (params.mode as 'vertical' | 'normal' | 'nearest' | 'horizontal') ?? 'vertical',
          min: (params.min as number) ?? -0.05,
          max: (params.max as number) ?? 0.05,
          decimate: (params.decimate as number) ?? 1,
          colormap: (params.colormap as string) ?? 'jet',
          verticalThreshold: (params.verticalThreshold as number) ?? 0.25,
          kNearestTriangles: (params.kNearestTriangles as number) ?? 16,
          signed: (params.signed as boolean) ?? true,
          ifcName: (params.ifcName as string) ?? '',
          ifcTypes: (params.ifcTypes as string) ?? '',
        }}
      />,
    );
  };

  const modeKey = params.mode as string | undefined;
  const meanDeviation = stats.meanDeviation as number | null | undefined;
  const keptWritten = stats.keptWritten as number | string | null | undefined;

  const timestampDate = job.completedAt
    ? new Date(job.completedAt)
    : new Date(job.createdAt);

  return (
    <div className='flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-slate-200/70 dark:border-stone-700/40'>
      <div className='flex items-center justify-center w-5 [&>svg]:size-5'>
        <StatusIcon status={job.status} />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='text-sm font-medium truncate text-slate-900 dark:text-stone-100'>{job.label}</div>
        <div className='text-xs text-slate-600 dark:text-stone-400'>
          {sourceLayer?.name ?? '—'} → {referenceLayer?.name ?? '—'}
          {modeKey && (
            <>
              {' · '}
              {t(`deviationAnalysis.modes.${modeKey}.label`, { defaultValue: modeKey })}
            </>
          )}
        </div>
      </div>
      <div className='text-xs text-slate-700 dark:text-stone-300 hidden md:block whitespace-nowrap'>
        {meanDeviation != null
          ? `${t('analyses.mean')}: ${Number(meanDeviation).toFixed(4)} m · ${keptWritten ?? '?'} ${t('analyses.points')}`
          : '—'}
      </div>
      <div className='text-xs text-slate-500 dark:text-stone-400 hidden lg:block whitespace-nowrap'>
        {formatDateTime(timestampDate)}
      </div>
      <Button variant='ghost' size='sm' onClick={handleRerun} disabled={!sourceLayer}>
        <RotateCw className='size-4' />
        <span className='hidden sm:inline ml-1'>{t('analyses.rerun')}</span>
      </Button>
    </div>
  );
}
